import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
    Button,
    IconButton,
    Paper,
    Slide,
    TextField,
    Tooltip,
    useTheme,
    List,
    ListItem,
    ListItemText,
} from '@material-ui/core'
import { Add } from '@material-ui/icons'

import { Container, StatusBadge } from '../../../components'
import {
    AreaConfig,
    IdBoolMap,
    IdentifiableNamedObject,
    InventoryConfig,
    ListInventoryAreaConfig,
    StatusGroup,
} from '../../../models'
import {
    RenderIAC,
    ReqIAC,
} from '../../InfrastructureConfig/InvAreaConfigDrawer'
import { useAppDispatch } from '../../../hooks'
import {
    CreateInventoryConfigRequest,
    RootState,
    createInventoryConfig,
    getAreaConfigList,
    updateInventoryConfigReq,
    UpdateInventoryConfigRequest,
} from '../../../store'
import {
    ADD_NEW_AREA_FORM,
    ADD_NEW_ITEM_FORM,
    ADD_NEW_STATUS_GROUP_FORM,
    INSPECTION_TYPE_FORM,
} from './ConfigureInspectionType'
import { AreaSelection } from './AreaSelection'
import { StatusGroupForm } from '../../StatusGroupConfig'
import { CreateNewAreaForm } from './CreateNewAreaForm'
import { axiosInstance } from '../../../helpers'

interface Props {
    subHeaderStyle: React.CSSProperties
    invItemsMap: IdBoolMap
    formPage: number
    invItemList: InventoryConfig[]
    largerView?: boolean
    setInvItemsMap: (map: IdBoolMap) => void
    setFormPage: (form: number) => void
    handleClose?: () => void
}

export const NewInvItemForm = (props: Props) => {
    const {
        subHeaderStyle,
        setFormPage,
        invItemsMap,
        setInvItemsMap,
        formPage,
        largerView,
        handleClose,
        invItemList,
    } = props

    const theme = useTheme()
    const dispatch = useAppDispatch()

    // Form state
    const [itemName, setItemName] = useState('')
    const [sku, setSKU] = useState('')
    const [description, setDescription] = useState('')
    const [doValidate, setDoValidate] = useState(false)
    const [selectedStatusGroup, setSelectedStatusGroup] = useState<
        StatusGroup | undefined
    >()
    const [IACList, setIACList] = useState<ListInventoryAreaConfig[]>([])
    const [IACIndex, setIACIndex] = useState<number | undefined>()

    // Similar items state
    const [similarInventoryConfigs, setSimilarInventoryConfigs] = useState<
        InventoryConfig[]
    >([])
    const [isNameFocused, setIsNameFocused] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingConfig, setEditingConfig] = useState<InventoryConfig | null>(
        null,
    )

    // Redux state
    const statusGroupList = useSelector(
        (state: RootState) => state.aptConfig.statusGroupList,
    )
    const areaConfigList = useSelector(
        (state: RootState) => state.aptConfig.areaConfigList,
    )

    const findSimilarInventoryConfigs = (name: string) => {
        if (!name.trim()) {
            setSimilarInventoryConfigs([])
            return
        }

        const similarConfigs = invItemList
            .filter(
                (config) =>
                    config.name.toLowerCase().includes(name.toLowerCase()) &&
                    config.name.toLowerCase() !== name.toLowerCase(),
            )
            .slice(0, 5) // Limit to 5 suggestions

        setSimilarInventoryConfigs(similarConfigs)
    }

    const handleItemNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.target.value
        setItemName(newName)
        findSimilarInventoryConfigs(newName)
    }

    const loadConfigForEditing = (config: InventoryConfig) => {
        setEditingConfig(config)
        setIsEditMode(true)
        setItemName(config.name)
        setSKU(config.sku)
        setDescription(config.description)

        // Get the status group from the statusGroupList
        if (config.status_group) {
            const matchingStatusGroup = statusGroupList?.find(
                (sg) => sg.id === config.status_group?.id,
            )
            if (matchingStatusGroup) {
                setSelectedStatusGroup(matchingStatusGroup)
            }
        }

        // Load the inventory area configs
        const params = {
            inventory_config: config.id,
        }
        axiosInstance
            .get(`apt_config/inventory_area_config/`, { params })
            .then((res) => {
                setIACList(res.data)
            })
            .catch((err) => {
                console.error('Error loading inventory area configs:', err)
                toast.error('Failed to load inventory area configurations')
            })

        setSimilarInventoryConfigs([])
    }

    const updateIAC = (newAreaConfig: AreaConfig) => {
        const newIACList: ListInventoryAreaConfig[] = []
        IACList.forEach((innerIAC, innerIDX) => {
            if (IACIndex !== innerIDX) {
                newIACList.push(innerIAC)
            } else {
                newIACList.push({ ...innerIAC, area_config: newAreaConfig })
            }
        })
        setIACList(newIACList)
    }

    const hasValidArea = IACList.some(
        (iac) => iac.area_config != null && iac.amount > 0,
    )

    const createInvItem = () => {
        if (!hasValidArea) {
            setDoValidate(true)
            return
        }

        if (isEditMode && editingConfig) {
            // Handle update case
            const req: UpdateInventoryConfigRequest = {
                id: editingConfig.id,
                req: {
                    name: itemName,
                    sku: sku,
                    description: description,
                },
            }

            if (selectedStatusGroup) {
                req.req.status_group = selectedStatusGroup.id
            }

            if (IACList.length !== 0) {
                const newIACList: ListInventoryAreaConfig[] = []
                IACList.forEach((innerIAC, innerIDX) => {
                    if (innerIAC.amount !== 0) {
                        newIACList.push(innerIAC)
                    }
                })

                let reqIACS = []
                reqIACS = newIACList.reduce<ReqIAC[]>((prev, iac) => {
                    if (iac.area_config && iac.amount > 0) {
                        return prev.concat({
                            inventory_config_id: editingConfig.id,
                            area_config_id: iac.area_config?.id ?? -1,
                            amount: iac.amount,
                        })
                    }
                    return prev
                }, [])

                req.req.iacs = reqIACS
            }

            dispatch(updateInventoryConfigReq(req)).then(() => {
                toast.success('Inventory Config successfully updated!')
                dispatch(getAreaConfigList({}))
                setFormPage(INSPECTION_TYPE_FORM)
                if (handleClose) {
                    handleClose()
                }
            })
        } else {
            // Handle create case
            if (!selectedStatusGroup) {
                return
            }

            const req: CreateInventoryConfigRequest = {
                name: itemName,
                sku: sku,
                description: description,
            }

            if (IACList.length !== 0) {
                const newIACList: ListInventoryAreaConfig[] = []
                IACList.forEach((innerIAC, innerIDX) => {
                    if (innerIAC.amount !== 0) {
                        newIACList.push(innerIAC)
                    }
                })

                let reqIACS = []
                reqIACS = newIACList.reduce<ReqIAC[]>((prev, iac) => {
                    if (iac.area_config && iac.amount > 0) {
                        return prev.concat({
                            inventory_config_id: -1,
                            area_config_id: iac.area_config?.id ?? -1,
                            amount: iac.amount,
                        })
                    }
                    return prev
                }, [])

                req['iacs'] = reqIACS
            }

            if (selectedStatusGroup) {
                req['status_group'] = selectedStatusGroup.id
            }

            dispatch(createInventoryConfig(req)).then((res) => {
                const tempInvItemsMap = invItemsMap
                tempInvItemsMap[res.data.id] = true
                setInvItemsMap(tempInvItemsMap)
                toast.success('Inventory Config successfully created!')
                dispatch(getAreaConfigList({}))
                setFormPage(INSPECTION_TYPE_FORM)
                if (handleClose) {
                    handleClose()
                }
            })
        }
    }

    const sortedAreaConfigList = useMemo(() => {
        const priorityList = ['Bed', 'DoBd', 'Bath', '1/2Ba', 'Com']

        return areaConfigList.sort((a, b) => {
            const aIndex = priorityList.indexOf(a.name)
            const bIndex = priorityList.indexOf(b.name)

            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex
            } else if (aIndex !== -1) {
                return -1
            } else if (bIndex !== -1) {
                return 1
            } else {
                return a.name.localeCompare(b.name)
            }
        })
    }, [areaConfigList])

    const statusGroupSelector = (
        <Container
            direction="column"
            style={{
                border: `1px solid ${
                    doValidate && !selectedStatusGroup && !isEditMode
                        ? theme.palette.error.main
                        : theme.palette.lightGrey.dark
                }`,
                borderRadius: '5px',
                marginBottom: theme.spacing(2),
            }}
        >
            <Container>
                <Container
                    direction="column"
                    style={{
                        height: '200px',
                        overflowY: 'auto',
                        borderRight: `1px solid ${theme.palette.lightGrey.dark}`,
                        flex: 1,
                    }}
                >
                    {statusGroupList?.map((statusGroup) => (
                        <Container
                            key={`STATUS_GROUP_SELECTION_${statusGroup.id}`}
                            style={{
                                fontSize: '15px',
                                fontWeight: 500,
                                padding: theme.spacing(1),
                                backgroundColor:
                                    statusGroup.id === selectedStatusGroup?.id
                                        ? theme.palette.lightGrey.dark
                                        : '',
                                cursor: 'pointer',
                            }}
                            onClick={() => setSelectedStatusGroup(statusGroup)}
                        >
                            {statusGroup.name}
                        </Container>
                    ))}
                </Container>
                <Container
                    justifyContent="flex-start"
                    style={{
                        flexWrap: 'wrap',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {selectedStatusGroup?.custom_statuses?.map((s) => (
                        <StatusBadge
                            key={s.id}
                            text={s.name}
                            customStyle={{
                                container: {
                                    margin: theme.spacing(0, 1, 1, 0),
                                    backgroundColor: s.color,
                                    width: 70,
                                    height: 40,
                                },
                                text: {
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                },
                            }}
                        />
                    ))}

                    {!selectedStatusGroup && (
                        <Container>Select a status group</Container>
                    )}
                </Container>
            </Container>

            <Container
                style={{
                    borderTop: `1px solid ${theme.palette.lightGrey.dark}`,
                }}
            >
                <Button
                    variant="contained"
                    style={{
                        margin: theme.spacing(1, 0, 1, 2),
                        backgroundColor: '#008C85',
                        color: 'white',
                        textTransform: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={() => setFormPage(ADD_NEW_STATUS_GROUP_FORM)}
                >
                    + New Status Group
                </Button>
            </Container>
        </Container>
    )

    if (formPage === ADD_NEW_STATUS_GROUP_FORM) {
        return (
            <Slide direction="left" in={formPage === ADD_NEW_STATUS_GROUP_FORM}>
                <Paper elevation={0} style={{ flex: 1 }}>
                    <Container
                        direction="column"
                        style={{
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        <StatusGroupForm
                            handleClose={() => setFormPage(ADD_NEW_ITEM_FORM)}
                            modalMode
                            setSelectedStatusGroup={setSelectedStatusGroup}
                            customLeftColorPlacement={-250}
                            customTopColorPlacement={0}
                            height={
                                largerView
                                    ? 'calc(100vh - 250px)'
                                    : 'calc(100vh - 300px)'
                            }
                        />
                    </Container>
                </Paper>
            </Slide>
        )
    } else if (formPage === ADD_NEW_AREA_FORM) {
        return (
            <Slide direction="left" in={true}>
                <Paper elevation={0} style={{ flex: 1 }}>
                    <Container
                        direction="column"
                        style={{
                            paddingRight: theme.spacing(1),
                        }}
                    >
                        <CreateNewAreaForm
                            subHeaderStyle={subHeaderStyle}
                            height={
                                largerView
                                    ? 'calc(100vh - 225px)'
                                    : 'calc(100vh - 300px)'
                            }
                            setFormPage={setFormPage}
                            updateIAC={updateIAC}
                        />
                    </Container>
                </Paper>
            </Slide>
        )
    }

    return (
        <Container direction="column" flex={1}>
            <Container
                direction="column"
                style={{
                    paddingRight: theme.spacing(1),
                }}
            >
                <Container style={{ flexDirection: 'column' }}>
                    <Container style={{ ...subHeaderStyle, fontSize: '20px' }}>
                        {isEditMode
                            ? 'Edit Inventory Item'
                            : 'Create New Inventory Item'}
                    </Container>
                </Container>
                <Container
                    style={{
                        flexDirection: 'column',
                        maxHeight: largerView
                            ? 'calc(100vh - 225px)'
                            : 'calc(100vh - 300px)',
                        minHeight: largerView
                            ? 'calc(100vh - 225px)'
                            : 'calc(100vh - 300px)',
                        overflowY: 'auto',
                    }}
                >
                    <Container style={{ ...subHeaderStyle }}>
                        Name Item (Required):
                    </Container>
                    <Container
                        style={{
                            marginBottom: theme.spacing(2),
                            position: 'relative',
                        }}
                    >
                        <TextField
                            error={doValidate && itemName === ''}
                            helperText={
                                doValidate && itemName === '' ? 'Required' : ''
                            }
                            variant="outlined"
                            value={itemName}
                            onChange={handleItemNameChange}
                            onFocus={() => setIsNameFocused(true)}
                            onBlur={() =>
                                setTimeout(() => setIsNameFocused(false), 200)
                            }
                            style={{
                                width: '100%',
                            }}
                            size="small"
                        />
                        {isNameFocused && similarInventoryConfigs.length > 0 && (
                            <Paper
                                elevation={3}
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    zIndex: 1000,
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                }}
                            >
                                <List dense>
                                    {similarInventoryConfigs.map((config) => (
                                        <ListItem
                                            key={config.id}
                                            button
                                            onClick={() =>
                                                loadConfigForEditing(config)
                                            }
                                        >
                                            <ListItemText
                                                primary={config.name}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Container>
                    <Container style={{ ...subHeaderStyle }}>SKU:</Container>
                    <Container style={{ marginBottom: theme.spacing(2) }}>
                        <TextField
                            variant="outlined"
                            value={sku}
                            onChange={(e) => setSKU(e.target.value)}
                            style={{
                                width: '100%',
                            }}
                            size="small"
                        />
                    </Container>
                    <Container style={{ ...subHeaderStyle }}>
                        Description:
                    </Container>
                    <Container style={{ marginBottom: theme.spacing(2) }}>
                        <TextField
                            variant="outlined"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={{
                                width: '100%',
                            }}
                            size="small"
                        />
                    </Container>
                    <Container style={{ ...subHeaderStyle }}>
                        Areas (Required):
                    </Container>
                    <Container
                        style={{
                            fontSize: '13px',
                            color: theme.palette.darkGreen.main,
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        Inventory will be add to areas across the property
                    </Container>
                    <Container
                        direction="column"
                        style={{
                            marginBottom: theme.spacing(2),
                            border:
                                doValidate && !hasValidArea
                                    ? `1px solid ${theme.palette.error.main}`
                                    : undefined,
                            borderRadius: '5px',
                            padding:
                                doValidate && !hasValidArea
                                    ? theme.spacing(1)
                                    : undefined,
                        }}
                    >
                        {IACList.map((iac, idx) => {
                            let configList: IdentifiableNamedObject[] = []
                            configList = sortedAreaConfigList.reduce<
                                AreaConfig[]
                            >((prev, areaConfig) => {
                                // if it is the config for this iac, return it
                                if (areaConfig.id === iac.area_config?.id) {
                                    return prev.concat(areaConfig)
                                }

                                // if this is the config for any other iac, do not return it
                                const isInOtherIAC = IACList.find(
                                    (iac) =>
                                        iac.area_config?.id === areaConfig.id,
                                )
                                if (isInOtherIAC) {
                                    return prev
                                }
                                return prev.concat(areaConfig)
                            }, [])

                            return (
                                <AreaSelection
                                    key={idx}
                                    iac={iac}
                                    updateIAC={(newIac) => {
                                        const newIACList: RenderIAC[] = []
                                        IACList.forEach(
                                            (innerIAC, innerIDX) => {
                                                if (idx !== innerIDX) {
                                                    newIACList.push(innerIAC)
                                                } else {
                                                    newIACList.push(newIac)
                                                }
                                            },
                                        )
                                        setIACList(newIACList)
                                    }}
                                    configOptions={configList}
                                    remove={() => {
                                        const newIACList: RenderIAC[] = []
                                        IACList.forEach(
                                            (innerIAC, innerIDX) => {
                                                if (idx !== innerIDX) {
                                                    newIACList.push(innerIAC)
                                                }
                                            },
                                        )
                                        setIACList(newIACList)
                                    }}
                                    onClick={() => {
                                        setFormPage(ADD_NEW_AREA_FORM)
                                        setIACIndex(idx)
                                    }}
                                />
                            )
                        })}
                        <Tooltip title="Add Inventory to Area">
                            <IconButton
                                onClick={() => {
                                    setIACList([...IACList, { amount: 1 }])
                                }}
                            >
                                <Add color={'primary'} fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </Container>
                    {doValidate && !hasValidArea && (
                        <Container
                            style={{
                                color: theme.palette.error.main,
                                fontSize: '12px',
                                marginTop: -theme.spacing(1),
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            Required
                        </Container>
                    )}
                    <Container style={{ ...subHeaderStyle }}>
                        Status Group (Required): {selectedStatusGroup?.name}
                    </Container>
                    {statusGroupSelector}
                    {doValidate && !selectedStatusGroup && !isEditMode && (
                        <Container
                            style={{
                                color: theme.palette.error.main,
                                fontSize: '12px',
                                marginTop: -theme.spacing(1),
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            Required
                        </Container>
                    )}
                </Container>
            </Container>
            <Container flex={1} />
            <Container style={{ justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    style={{
                        textTransform: 'none',
                        margin: theme.spacing(1, 0, 1, 2),
                    }}
                    onClick={() => {
                        setFormPage(INSPECTION_TYPE_FORM)
                        setSelectedStatusGroup(undefined)
                        if (handleClose) {
                            handleClose()
                        }
                    }}
                >
                    {handleClose ? 'Cancel' : 'Back'}
                </Button>
                <Button
                    variant="contained"
                    style={{
                        margin: theme.spacing(1, 0, 1, 2),
                        backgroundColor: '#008C85',
                        color: 'white',
                        textTransform: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={() => {
                        const isNameInvalid = itemName === ''
                        const isStatusGroupInvalid =
                            !isEditMode && !selectedStatusGroup
                        const isAreaInvalid = !hasValidArea

                        if (
                            isNameInvalid ||
                            isStatusGroupInvalid ||
                            isAreaInvalid
                        ) {
                            setDoValidate(true)
                        } else {
                            setDoValidate(false)
                            createInvItem()
                            setSelectedStatusGroup(undefined)
                        }
                    }}
                >
                    {isEditMode ? 'Update' : 'Create'}
                </Button>
            </Container>
        </Container>
    )
}
